import React from 'react';
import Icon from '../../../components/AppIcon';

const CompanyInfoForm = ({ formData, errors, onChange }) => {
  const handleChange = (field, value) => {
    onChange(field, value);
  };

  const formatRfc = (value) => {
    const cleaned = value?.replace(/[^A-Za-z0-9]/g, '')?.toUpperCase();
    return cleaned?.slice(0, 13);
  };

  const formatPostalCode = (value) => {
    return value?.replace(/[^0-9]/g, '')?.slice(0, 5);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
          <Icon name="Building2" className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Información de la Empresa</h2>
          <p className="text-sm text-gray-500">Datos fiscales y de contacto del taller</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Nombre Comercial del Taller <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData?.companyName || ''}
            onChange={(e) => handleChange('companyName', e?.target?.value)}
            placeholder="Ej: Taller Mecánico García"
            className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
              errors?.companyName ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
            }`}
          />
          {errors?.companyName && (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <Icon name="AlertCircle" className="w-3.5 h-3.5" />
              {errors?.companyName}
            </p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Razón Social
          </label>
          <input
            type="text"
            value={formData?.legalName || ''}
            onChange={(e) => handleChange('legalName', e?.target?.value)}
            placeholder="Ej: García Automotriz S.A. de C.V."
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
          <p className="mt-1 text-xs text-gray-500">Opcional. Si es diferente al nombre comercial.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            RFC / Tax ID <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData?.rfc || ''}
            onChange={(e) => handleChange('rfc', formatRfc(e?.target?.value))}
            placeholder="GARC850101ABC"
            maxLength={13}
            className={`w-full px-4 py-2.5 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors uppercase ${
              errors?.rfc ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
            }`}
          />
          {errors?.rfc ? (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <Icon name="AlertCircle" className="w-3.5 h-3.5" />
              {errors?.rfc}
            </p>
          ) : (
            <p className="mt-1 text-xs text-gray-500">Formato: AAAA######XXX (12-13 caracteres)</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Teléfono de Contacto
          </label>
          <input
            type="tel"
            value={formData?.phone || ''}
            onChange={(e) => handleChange('phone', e?.target?.value)}
            placeholder="+52 55 1234 5678"
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Dirección <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData?.address || ''}
            onChange={(e) => handleChange('address', e?.target?.value)}
            placeholder="Calle, Número, Colonia"
            className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
              errors?.address ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
            }`}
          />
          {errors?.address && (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <Icon name="AlertCircle" className="w-3.5 h-3.5" />
              {errors?.address}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Ciudad <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData?.city || ''}
            onChange={(e) => handleChange('city', e?.target?.value)}
            placeholder="Ciudad de México"
            className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
              errors?.city ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
            }`}
          />
          {errors?.city && (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <Icon name="AlertCircle" className="w-3.5 h-3.5" />
              {errors?.city}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Estado <span className="text-red-500">*</span>
          </label>
          <select
            value={formData?.state || ''}
            onChange={(e) => handleChange('state', e?.target?.value)}
            className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
              errors?.state ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
            }`}
          >
            <option value="">Seleccionar estado</option>
            {['Aguascalientes','Baja California','Baja California Sur','Campeche','Chiapas','Chihuahua','Ciudad de México','Coahuila','Colima','Durango','Estado de México','Guanajuato','Guerrero','Hidalgo','Jalisco','Michoacán','Morelos','Nayarit','Nuevo León','Oaxaca','Puebla','Querétaro','Quintana Roo','San Luis Potosí','Sinaloa','Sonora','Tabasco','Tamaulipas','Tlaxcala','Veracruz','Yucatán','Zacatecas']?.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {errors?.state && (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <Icon name="AlertCircle" className="w-3.5 h-3.5" />
              {errors?.state}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Código Postal <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData?.postalCode || ''}
            onChange={(e) => handleChange('postalCode', formatPostalCode(e?.target?.value))}
            placeholder="06600"
            maxLength={5}
            className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
              errors?.postalCode ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
            }`}
          />
          {errors?.postalCode && (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <Icon name="AlertCircle" className="w-3.5 h-3.5" />
              {errors?.postalCode}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyInfoForm;
